/**
 * The Home page's one network call, and the mapping from what it returns to what
 * the page's components take.
 *
 *   /home-summary   ours. The three stat tiles, the Data Source Usage donut and
 *                   the Recent Dashboards & Reports table, in one request.
 *                   Paramless — the user comes off the accessToken.
 *
 * The endpoint returns raw domain values (boardType, publish, counts) rather
 * than display strings, so everything cosmetic — labels, badge text, colours,
 * percentages — is decided here instead.
 *
 * The Scheduled Reports calendar is not here — it belongs to the scheduler
 * service, and Home reaches it through ScheduleReports/scheduleApi.js rather
 * than through ours. See homeSchedules.js.
 */

import { getUserById } from "../../api/orgRegistry";

const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;
// const REACT_APP_CONNECTION_INFO_BASE_URL = "http://localhost:8080";

export async function fetchHomeSummary() {
  const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/home-summary`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  });

  const body = await response.json();

  if (!response.ok || !body.success) {
    throw new Error(body.message || `Could not load the home summary (${response.status})`);
  }

  return body.data;
}

/* ── Stat tiles ──────────────────────────────────────────────── */

/* The tiles' colours and icons live in index.js, where they always did; this
   only supplies the three numbers, in the order the tiles are declared.
   Dashboards and Reports are counted separately by the API because the Recent
   table needs the distinction — the tile wants them added up. */
export const statAmounts = (stats) => ({
  groups: stats ? stats.groups : null,
  resources: stats ? stats.resources : null,
  boards: stats ? stats.dashboards + stats.reports : null,
});

/* ── Publication Overview ────────────────────────────────────── */

/* Bars beyond this are summed into one "Other Apps" row, so the card cannot grow
   past five lines however many applications the org licenses. */
const PUBLICATION_TOP_N = 4;

const PUBLICATION_COLORS = ["#4F46E5", "#A855F7", "#14B8A6", "#F97316", "#94A3B8"];

const OTHER_APPS_LABEL = "Other Apps";

/**
 * Published boards per application, as the card renders them.
 *
 * The API sends `{ appId, count }` and a `publishedTotal`; the application's
 * name is not its to know. Names come from the App Registry list DashboardView
 * already loads into redux as `state.user.apps.Result`, keyed by
 * `ApplicationID` — the same resolution the Resources page does for its
 * Application column.
 *
 * An id with no matching registry entry keeps the raw id rather than showing a
 * dash: it means a board pointing at an application this user cannot see, and
 * hiding that makes the card look like it lost rows.
 *
 * The denominator is `publishedTotal`, not the sum of the counts. Those differ
 * the moment a board belongs to two applications — it is counted under each —
 * and "42 of the 86 published boards touch ElectriPOS" stays true where "42 of
 * 94 app-slots" would mean nothing to anybody.
 */
export const publicationData = (publicationOverview, publishedTotal, apps) => {
  const rows = publicationOverview || [];
  if (rows.length === 0) return [];

  const nameById = new Map((apps || []).map((app) => [app.ApplicationID, app.Name]));
  const total = publishedTotal || rows.reduce((sum, row) => sum + row.count, 0);

  const named = rows.map((row) => ({
    appId: row.appId,
    name: nameById.get(row.appId) || row.appId,
    count: row.count,
  }));

  /* Already sorted by the API, but sorted again here so the card does not depend
     on that — the top-N cut is only meaningful on an ordered list. */
  const ordered = [...named].sort((a, b) => b.count - a.count);
  const head = ordered.slice(0, PUBLICATION_TOP_N);
  const tail = ordered.slice(PUBLICATION_TOP_N);

  const shown =
    tail.length > 0
      ? [...head, { appId: "__other__", name: OTHER_APPS_LABEL, count: tail.reduce((s, r) => s + r.count, 0) }]
      : head;

  return shown.map((row, index) => ({
    ...row,
    percent: total > 0 ? Math.round((row.count / total) * 100) : 0,
    color: PUBLICATION_COLORS[index] || PUBLICATION_COLORS[PUBLICATION_COLORS.length - 1],
  }));
};

/* ── Data Source Usage donut ─────────────────────────────────── */

/* Presentation only. The API sends `{ key, name, count }` per type and omits
   types with no resources, so a palette keyed by type — rather than a list
   zipped positionally against the data — is what keeps REST API the same navy
   whether or not MySQL happens to exist. */
const TYPE_COLORS = {
  restapi: "#1E3A5F",
  mysql: "#F59E0B",
  graphql: "#94A3B8",
  mongodb: "#2DD4BF",
};

const FALLBACK_COLOR = "#CBD5E1";

/* DataSourceDonut's legend prints `value` with a % sign after it, so `value` has
   to be the percentage, not the count — see the note in DataSourceChart.js.
   `count` is passed through as well, for the tooltip. */
export const donutData = (dataSourceUsage) => {
  const rows = dataSourceUsage || [];
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return rows.map((row) => ({
    name: row.name,
    count: row.count,
    /* No rounding to 100: three equal types read 33% each rather than one of
       them silently absorbing the remainder. The donut's own arcs are drawn
       from these same numbers, so they stay in proportion regardless. */
    value: total > 0 ? Math.round((row.count / total) * 100) : 0,
    color: TYPE_COLORS[row.key] || FALLBACK_COLOR,
  }));
};

/* ── Recent Dashboards & Reports ─────────────────────────────── */

/* There is no Archived state in the board model — only `publish` — so
   SelectItemTable's third status stays unused rather than being faked. */
const statusOf = (publish) => (publish ? "Published" : "Draft");

/* boardType is "reports" (plural) or "dashboard"; the table shows a singular
   label and picks its icon off the same value. */
const typeOf = (boardType) => (boardType === "reports" ? "Report" : "Dashboard");

/* Only the creator's id is on the board — `userID`, written at save time and
   never reassigned, so it is the board's author rather than a separate notion of
   ownership. Every user in the org is already fetched and cached in redux as
   user.otherUsers by DashboardView, so the join happens here rather than costing
   a request. A board shared in from another org was created by someone who is
   not in that list, and shows a dash rather than a raw UUID. */
/* `UserName` is the field the cached list actually carries — its entries come
   from the org registry's permission endpoint, not the login user record, so
   there is no FirstName/LastName on them. It is also what the share picker in
   AddBoardGroup displays for the very same list, so Home and the sharing UI name
   people identically. FirstName/LastName/Email are kept as fallbacks because the
   per-user endpoint below returns the fuller record. */
const nameFromDetails = (details) => {
  if (!details) return null;
  const fullName = [details.FirstName, details.LastName].filter(Boolean).join(" ").trim();
  return details.UserName || fullName || details.Email || null;
};

const createdByName = (createdById, otherUsers, fetchedNames) => {
  const match = (otherUsers || []).find((u) => u && u.UserID === createdById);
  const cached = nameFromDetails(match && match.UserDetails);
  if (cached) return cached;

  return (fetchedNames && fetchedNames[createdById]) || "—";
};

/**
 * Names for the creators the cached org list cannot account for.
 *
 * `otherUsers` only covers the *current* org, so it can never name someone who
 * has left it, or the creator of a board shared in from another org — and those
 * are exactly the rows that were showing a dash. The Org Registry will resolve
 * any id directly, so the few that are missing get looked up one by one.
 *
 * Bounded work: the table is five rows, the ids are deduplicated, and anyone
 * already in the cached list is skipped — so this is usually zero requests and
 * at worst five. A lookup that fails leaves that row on its dash rather than
 * failing the page; one departed user should not blank the other four names.
 */
export async function fetchCreatorNames(recent, otherUsers) {
  const cached = new Set((otherUsers || []).filter((u) => u && nameFromDetails(u.UserDetails)).map((u) => u.UserID));

  const missing = [...new Set((recent || []).map((row) => row.createdById).filter((id) => id && !cached.has(id)))];

  if (missing.length === 0) return {};

  const userToken = localStorage.getItem("accessToken");

  const entries = await Promise.all(
    missing.map(async (id) => {
      try {
        return [id, nameFromDetails(await getUserById(id, { userToken }))];
      } catch (error) {
        console.log("could not resolve creator===>>", id, error);
        return [id, null];
      }
    }),
  );

  return entries.reduce((names, [id, name]) => {
    if (name) names[id] = name;
    return names;
  }, {});
}

const formatUpdated = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  /* "May 6, 2025 11:42 AM" — the format the table's mock rows used. */
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const recentItems = (recent, otherUsers, fetchedNames) =>
  (recent || []).map((row) => ({
    id: row.id,
    name: row.name,
    type: typeOf(row.boardType),
    subtype: typeOf(row.boardType),
    createdBy: createdByName(row.createdById, otherUsers, fetchedNames),
    status: statusOf(row.publish),
    timestamp: formatUpdated(row.updatedAt),
  }));
